import {
  isFieldScored,
  isQuestionScored,
  isQuestionVisible,
  type AnswerMap,
  type SampleEntryValue,
  type TemplateDefinition,
} from './template.js';

/**
 * Per-audit failure summary (Phase 6b): which scored questions passed or
 * failed, written to Audits.FailureSummary when an audit is submitted, so
 * portfolio analytics (question failure rates, section breakdowns) read one
 * Audits row per audit instead of scanning AuditResponses (~200 rows each).
 *
 * Mirrors scoring.ts exactly: only visible, scored questions; a Yes/No answer
 * counts, N/A and unanswered don't. Sample groups record their checked items
 * as [passed, failed] counts.
 *
 *   { "v": 1, "q": { "<questionId>": "p" | "f" }, "g": { "<questionId>": [passed, failed] } }
 */

export interface FailureSummary {
  v: 1;
  /** Single Yes/No questions: p = passed, f = failed. */
  q: Record<string, 'p' | 'f'>;
  /** Sample groups: [passed items, failed items] across all entries. */
  g: Record<string, [number, number]>;
}

export function summarizeAudit(def: TemplateDefinition, answers: AnswerMap): FailureSummary {
  const out: FailureSummary = { v: 1, q: {}, g: {} };
  for (const section of def.sections ?? []) {
    for (const q of section.questions ?? []) {
      if (!isQuestionScored(q) || !isQuestionVisible(def, section, q, answers)) continue;
      const value = Object.prototype.hasOwnProperty.call(answers, q.id) ? answers[q.id] : undefined;
      if (q.type === 'sample_group') {
        if (!Array.isArray(value)) continue;
        let passed = 0;
        let failed = 0;
        for (const entry of value as SampleEntryValue[]) {
          if (!entry || typeof entry !== 'object') continue;
          for (const field of q.fields ?? []) {
            if (!isFieldScored(field)) continue;
            const v = entry[field.id];
            if (v === 'yes') passed++;
            else if (v === 'no') failed++;
          }
        }
        if (passed + failed > 0) put(out.g, q.id, [passed, failed]);
      } else if (value === 'yes') {
        put(out.q, q.id, 'p');
      } else if (value === 'no') {
        put(out.q, q.id, 'f');
      }
    }
  }
  return out;
}

/** defineProperty: a hand-edited "__proto__" id must not replace the prototype. */
function put<T>(obj: Record<string, T>, key: string, value: T) {
  Object.defineProperty(obj, key, { value, enumerable: true, writable: true, configurable: true });
}

/**
 * Read a stored summary defensively (the column is member-writable, risk #1):
 * anything malformed is dropped, never thrown. null = no usable summary.
 */
export function parseFailureSummary(raw: string | null | undefined): FailureSummary | null {
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== 'object' || (parsed as { v?: unknown }).v !== 1) return null;
  const src = parsed as { q?: unknown; g?: unknown };
  const out: FailureSummary = { v: 1, q: {}, g: {} };
  if (src.q && typeof src.q === 'object') {
    for (const [k, v] of Object.entries(src.q as Record<string, unknown>)) {
      if (v === 'p' || v === 'f') put(out.q, k, v);
    }
  }
  if (src.g && typeof src.g === 'object') {
    for (const [k, v] of Object.entries(src.g as Record<string, unknown>)) {
      if (
        Array.isArray(v) &&
        v.length === 2 &&
        v.every((n) => typeof n === 'number' && Number.isInteger(n) && n >= 0 && n <= 10_000)
      ) {
        put(out.g, k, [v[0] as number, v[1] as number]);
      }
    }
  }
  return out;
}
